import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Plus, Trash2, Download, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const STATUS_CLS = {
  submitted: "text-nc-blue border-nc-blue/40 bg-nc-blue/10",
  approved: "text-nc-orange border-nc-orange/40 bg-nc-orange/10",
  paid: "text-nc-green border-nc-green/40 bg-nc-green/10",
  rejected: "text-red-400 border-red-500/40 bg-red-500/10",
};

export default function InvoicesTab({ onboarded }) {
  const [invoices, setInvoices] = useState([]);
  const [mode, setMode] = useState("list");
  const [items, setItems] = useState([{ description: "", qty: 1, rate: 0, unit: "hours" }]);
  const [form, setForm] = useState({ job_summary: "", proof_links: "", payment_method: "bank", payment_details: "", period: "", display_currency: "AUD" });
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/dev/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);

  const setItem = (idx, field, value) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const submit = async () => {
    setSending(true);
    try {
      const r = await api.post("/dev/invoices", {
        ...form,
        line_items: items.map((i) => ({ ...i, qty: Number(i.qty), rate: Number(i.rate) })),
      });
      toast.success(`Invoice ${r.data.invoice_number} submitted`);
      setMode("list");
      setItems([{ description: "", qty: 1, rate: 0, unit: "hours" }]);
      setForm({ job_summary: "", proof_links: "", payment_method: "bank", payment_details: "", period: "", display_currency: "AUD" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const valid = items.every((i) => i.description.trim().length >= 3 && Number(i.qty) > 0 && Number(i.rate) >= 0)
    && form.job_summary.trim().length >= 10 && form.payment_details.trim().length >= 5;

  return (
    <div data-testid="invoices-tab" className="flex flex-col gap-5 max-w-4xl">
      {!onboarded && (
        <div data-testid="invoices-locked" className="border border-nc-orange/40 bg-nc-orange/5 p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-nc-orange shrink-0 mt-0.5" />
          <p className="font-mono text-xs text-zinc-300 leading-relaxed">
            Invoicing unlocks once your NDA and IP Assignment are signed — complete the Onboarding tab first. This keeps every payment legally linked to your agreements.
          </p>
        </div>
      )}

      {mode === "list" ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Your invoices ({invoices.length})</p>
            <button data-testid="invoice-new-button" onClick={() => setMode("new")} disabled={!onboarded} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-3 flex items-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> New Invoice
            </button>
          </div>
          {invoices.length === 0 && (
            <div className="border border-dashed border-white/15 p-14 text-center">
              <Receipt className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="font-mono text-xs text-zinc-500">No invoices yet. Submit one when you're ready to request payment.</p>
            </div>
          )}
          {invoices.map((inv, i) => (
            <div key={inv.id} data-testid={`invoice-${i + 1}`} className="border border-white/10 bg-black/40 p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-sans font-bold text-sm text-zinc-100">{inv.invoice_number}</p>
                  <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${STATUS_CLS[inv.status]}`}>{inv.status}</span>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 mt-1.5">
                  {inv.created_at.slice(0, 10)} // {inv.line_items.length} item{inv.line_items.length === 1 ? "" : "s"} {inv.period ? `// ${inv.period}` : ""}
                </p>
                <p className="font-mono text-xs text-zinc-400 mt-1 truncate max-w-md">{inv.job_summary}</p>
              </div>
              <div className="flex items-center gap-4">
                <p className="font-sans font-bold text-lg text-nc-green">AUD {inv.total.toFixed(2)}</p>
                {inv.display_currency && inv.display_currency !== "AUD" && inv.converted_total != null && (
                  <p data-testid="invoice-converted" className="font-mono text-[9px] text-nc-blue mt-0.5">≈ {inv.display_currency} {inv.converted_total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                )}
                <a data-testid={`invoice-pdf-${i + 1}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} onClick={async (e) => {
                  e.preventDefault();
                  const token = localStorage.getItem("nc_token");
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${inv.invoice_number}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                }} className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 border border-nc-blue/40 px-3 py-2 flex items-center gap-1.5 transition-colors">
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div data-testid="invoice-form" className="border border-white/10 bg-black/40 p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <h3 className="font-sans font-bold uppercase text-lg text-zinc-50 flex items-center gap-2"><Receipt className="w-5 h-5 text-nc-blue" /> New Invoice</h3>
            <button data-testid="invoice-cancel" onClick={() => setMode("list")} className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-zinc-100">Cancel</button>
          </div>

          <div className="flex flex-col gap-3">
            <p className={labelCls}>Line items (AUD)</p>
            {items.map((item, i) => (
              <div key={i} data-testid={`invoice-item-${i + 1}`} className="grid grid-cols-[1fr_90px_110px_100px_40px] gap-2 items-end">
                <div>
                  {i === 0 && <label className={labelCls}>Description</label>}
                  <input data-testid={`invoice-item-${i + 1}-desc`} className={inputCls} placeholder="Work performed…" value={item.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                </div>
                <div>
                  {i === 0 && <label className={labelCls}>Qty</label>}
                  <input data-testid={`invoice-item-${i + 1}-qty`} type="number" min="0" step="0.5" className={inputCls} value={item.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                </div>
                <div>
                  {i === 0 && <label className={labelCls}>Unit</label>}
                  <select data-testid={`invoice-item-${i + 1}-unit`} className={inputCls} value={item.unit} onChange={(e) => setItem(i, "unit", e.target.value)}>
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                    <option value="milestone">milestone</option>
                    <option value="item">item</option>
                  </select>
                </div>
                <div>
                  {i === 0 && <label className={labelCls}>Rate</label>}
                  <input data-testid={`invoice-item-${i + 1}-rate`} type="number" min="0" className={inputCls} value={item.rate} onChange={(e) => setItem(i, "rate", e.target.value)} />
                </div>
                <button data-testid={`invoice-item-${i + 1}-remove`} onClick={() => setItems((arr) => arr.length > 1 ? arr.filter((_, x) => x !== i) : arr)} className="h-[38px] border border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-400/40 flex items-center justify-center transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button data-testid="invoice-add-item" onClick={() => setItems((arr) => [...arr, { description: "", qty: 1, rate: 0, unit: "hours" }])} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 hover:bg-nc-blue/10 px-3 py-2 flex items-center gap-2 transition-colors">
              <Plus className="w-3 h-3" /> Add item
            </button>
            <p data-testid="invoice-total" className="font-sans font-bold text-lg text-nc-green text-right">Total: AUD {total.toFixed(2)} <span className="font-mono text-[10px] text-zinc-500 font-normal">(no GST — company not GST-registered)</span></p>
          </div>

          <div>
            <label className={labelCls}>Job summary * (contract & work details — be thorough)</label>
            <textarea data-testid="invoice-summary" rows={8} className={`${inputCls} resize-y leading-relaxed`} value={form.job_summary} onChange={(e) => setForm((f) => ({ ...f, job_summary: e.target.value }))} placeholder="Full detail helps get you paid faster — reference your contract scope, sprint/milestone, deliverables completed, changesets, builds reviewed, and anything the studio needs to verify the work.&#10;&#10;e.g. Alpha 0.4 sprint (contract: hourly UE5 gameplay) — vault door replication system delivered and reviewed in cs:128-131, prison cinematic camera polish approved by lead, 2 build regressions fixed…" />
            <p className="font-mono text-[9px] text-zinc-600 mt-1.5 text-right">{form.job_summary.length} / 2000</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Work period</label>
              <input data-testid="invoice-period" className={inputCls} value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} placeholder="e.g. 28 Jul – 04 Aug 2026" />
            </div>
            <div>
              <label className={labelCls}>Proof of work (links / changesets)</label>
              <input data-testid="invoice-proof" className={inputCls} value={form.proof_links} onChange={(e) => setForm((f) => ({ ...f, proof_links: e.target.value }))} placeholder="Changeset IDs, video links, build notes" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payment method *</label>
              <select data-testid="invoice-method" className={inputCls} value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}>
                <option value="bank">Bank transfer (BSB + account)</option>
                <option value="paypal">PayPal</option>
                <option value="payoneer">Payoneer</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{form.payment_method === "bank" ? "BSB + Account number *" : form.payment_method === "payoneer" ? "Payoneer email or account ID *" : "PayPal email *"}</label>
              <input data-testid="invoice-payment-details" className={inputCls} value={form.payment_details} onChange={(e) => setForm((f) => ({ ...f, payment_details: e.target.value }))} placeholder={form.payment_method === "bank" ? "BSB 000-000 ACC 00000000" : "you@email.com"} />
            </div>
          </div>
          <div className="max-w-sm">
            <label className={labelCls}>PDF display currency (invoice stays payable in AUD)</label>
            <select data-testid="invoice-display-currency" className={inputCls} value={form.display_currency} onChange={(e) => setForm((f) => ({ ...f, display_currency: e.target.value }))}>
              {["AUD", "USD", "EUR", "GBP", "CAD", "NZD"].map((c) => <option key={c} value={c}>{c}{c === "AUD" ? " — no conversion shown" : " — shown as indicative equivalent on PDF"}</option>)}
            </select>
          </div>
          <button data-testid="invoice-submit" onClick={submit} disabled={sending || !valid} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors">
            {sending ? "Submitting…" : "Submit Invoice for Payment"}
          </button>
        </div>
      )}
    </div>
  );
}
